import { AlertTriangle, Settings, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/hooks/use-settings";
import { useRepairStorage } from "@/hooks/use-storage-health";
import { useAuthStore } from "@/stores/auth.store";
import { CURRENT_STORAGE_SCHEMA_VERSION } from "@shared";
import { Button } from "@/components/ui/button";

// ═══════════════════════════════════════════════════════════
// StorageSchemaBanner — App-wide admin alert when the tenant's
// B24 Data Storage schema is behind CURRENT_STORAGE_SCHEMA_VERSION.
//
// Only visible to admins. Dismissed only when schema is repaired —
// never dismissible by click. Piggybacks on the existing useSettings()
// fetch so no additional API call is needed.
// ═══════════════════════════════════════════════════════════

export function StorageSchemaBanner() {
  const { t } = useTranslation("common");
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const { data: settings } = useSettings();
  const repairStorage = useRepairStorage();

  // Only show to admins with loaded settings whose schema is stale
  if (!isAdmin || !settings) return null;
  if ((settings.storageSchemaVersion ?? 0) >= CURRENT_STORAGE_SCHEMA_VERSION) return null;

  const handleRepair = () => {
    repairStorage.mutate("ADD_MISSING_PROPERTIES");
  };

  return (
    <div className="flex flex-col gap-1 border-b border-amber-500/30 bg-amber-500/10 px-6 py-3 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex items-center gap-2 shrink-0">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
        <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
          {t("storageBanner.title")}
        </span>
      </div>
      <p className="flex-1 text-sm text-amber-600 dark:text-amber-500">{t("storageBanner.desc")}</p>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="outline"
          className="border-amber-500/50 text-amber-700 hover:bg-amber-500/20 dark:text-amber-400 dark:hover:bg-amber-500/20"
          disabled={repairStorage.isPending}
          onClick={handleRepair}
          aria-label={t("storageBanner.repairNow")}
        >
          <Wrench className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {repairStorage.isPending ? t("storageBanner.repairing") : t("storageBanner.repairNow")}
        </Button>
        <Button size="sm" variant="ghost" className="text-amber-700 dark:text-amber-400" asChild>
          <Link to="/settings">
            <Settings className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t("storageBanner.viewSettings")}
          </Link>
        </Button>
      </div>
    </div>
  );
}
